import React, { useEffect, useRef, useState } from "react";
import ReactCrop, {
    type Crop,
    type PercentCrop,
    type PixelCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button } from "../misc/Button";
import { CORPORATE_HEADSHOT_MIN_DIMENSION } from "../../utils/imageValidation";

// Touch-device override: react-image-crop's default 9×9 drag handles are too
// small for a finger. Scale them up only on coarse-pointer devices so desktop
// is unaffected. Class names cover both v10 (.ord-handle) and v11 (.ReactCrop__drag-handle).
const TOUCH_HANDLE_CSS = `
    @media (pointer: coarse) {
        .ReactCrop__drag-handle,
        .ord-handle {
            transform: scale(2.4);
        }
    }
`;

interface CorporateHeadshotCropModalProps {
    file: File;
    onConfirm: (blob: Blob) => void;
    onCancel: () => void;
}

const CorporateHeadshotCropModal: React.FC<CorporateHeadshotCropModalProps> = ({
    file,
    onConfirm,
    onCancel,
}) => {
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    // Display-pixel equivalents of the 600 source-px floor, recalculated
    // every time the image (or its layout size) changes. Drag handles use
    // these so the box physically can't shrink past the quality threshold.
    const [minDisplay, setMinDisplay] = useState<{ w: number; h: number }>({
        w: 50,
        h: 50,
    });
    const imgRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
        // Materialize bytes upfront for the same reason getImageDimensions
        // does — on Android, File handles backed by content URIs can fail to
        // feed an image element a blob: URL directly. Re-blobbing from the ArrayBuffer
        // gives us a self-contained source the cropper can render reliably.
        let url: string | null = null;
        let cancelled = false;
        (async () => {
            try {
                const buf = await file.arrayBuffer();
                if (cancelled) return;
                const blob = new Blob([buf], { type: file.type || "image/jpeg" });
                url = URL.createObjectURL(blob);
                setImageSrc(url);
            } catch {
                // Upfront validation should have caught this. If we still get
                // here, close the modal so the user isn't stuck on a blank box.
                if (!cancelled) onCancel();
            }
        })();
        return () => {
            cancelled = true;
            if (url) URL.revokeObjectURL(url);
        };
    }, [file, onCancel]);

    // On image load: compute the display-pixel min that maps to 600 source px,
    // seed a default crop sized to the 8:9 slideshow photo slot (640x720 inside
    // the 1280x720 canvas), and place it near the top so the initial selection
    // is visible without scrolling on tall portraits. User can drag past it
    // into any square-or-portrait shape; only the starting box is opinionated.
    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        const { width, height, naturalWidth, naturalHeight } = img;
        const scaleX = naturalWidth / width;
        const scaleY = naturalHeight / height;
        const minDispW = CORPORATE_HEADSHOT_MIN_DIMENSION / scaleX;
        const minDispH = CORPORATE_HEADSHOT_MIN_DIMENSION / scaleY;
        setMinDisplay({ w: minDispW, h: minDispH });

        // Largest 8:9 box that fits within ~85% of the image.
        const aspect = 8 / 9; // width / height
        let cropW = Math.min(width * 0.85, height * 0.85 * aspect);
        let cropH = cropW / aspect;
        // Enforce the source-pixel floor on BOTH axes WITHOUT breaking 8:9 —
        // scale the whole box up so neither side is below its minimum. Clamping
        // each axis independently distorts the ratio (e.g. a 600px-wide source
        // collapsed the default box to a square, so it no longer filled the
        // 8:9 photo slot). The crop stays free-form; only this starting box is
        // pinned to 8:9.
        const floorScale = Math.max(1, minDispW / cropW, minDispH / cropH);
        cropW *= floorScale;
        cropH *= floorScale;
        // Never exceed the image; keep 8:9 if we have to shrink to fit.
        const fitScale = Math.min(1, width / cropW, height / cropH);
        cropW *= fitScale;
        cropH *= fitScale;

        const initial: PixelCrop = {
            unit: "px",
            width: cropW,
            height: cropH,
            x: Math.max(0, (width - cropW) / 2),
            y: Math.min(Math.max(0, (height - cropH) / 2), 20),
        };
        setCrop(initial);
        setCompletedCrop(initial);
    };

    // Coerce any wider-than-tall selection to square. Applied to both onChange
    // (live drag) and onComplete (drag end) — react-image-crop passes the two
    // handlers independent crop objects, so the on-end value isn't already
    // clamped just because the on-change handler clamped the in-progress one.
    const clampToSquareOrPortrait = (c: PixelCrop): PixelCrop =>
        c.width > c.height ? { ...c, width: c.height } : c;

    const handleCropChange = (next: PixelCrop, _percent: PercentCrop) => {
        setCrop(clampToSquareOrPortrait(next));
        setError(null);
    };

    const handleApply = () => {
        if (!completedCrop || !imgRef.current || !completedCrop.width) {
            setError("Please select a crop area.");
            return;
        }
        const image = imgRef.current;
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const sx = completedCrop.x * scaleX;
        const sy = completedCrop.y * scaleY;
        // Coerce sub-pixel rounding to a true square-or-portrait box. The live
        // clamp keeps the selection visually square, but floating-point math
        // through display→source scaling can leave the width fractionally
        // larger than the height — the user perceives that as square, so we
        // honor that perception instead of rejecting it.
        const sh = completedCrop.height * scaleY;
        const sw = Math.min(completedCrop.width * scaleX, sh);

        if (sw < CORPORATE_HEADSHOT_MIN_DIMENSION || sh < CORPORATE_HEADSHOT_MIN_DIMENSION) {
            setError(
                `Crop area is too small. Make the selection larger so the photo stays sharp in your videos.`
            );
            return;
        }

        setIsProcessing(true);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(sw);
        canvas.height = Math.round(sh);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            setIsProcessing(false);
            setError("Could not process image. Try again.");
            return;
        }
        ctx.drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh);
        canvas.toBlob(
            (blob) => {
                setIsProcessing(false);
                if (!blob) {
                    setError("Could not export image. Try again.");
                    return;
                }
                onConfirm(blob);
            },
            "image/jpeg",
            0.92
        );
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4 pb-[calc(env(safe-area-inset-bottom)+90px)] sm:pb-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="crop-modal-title"
        >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl h-[75vh] overflow-hidden flex flex-col">
                <div className="px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-200">
                    <h2
                        id="crop-modal-title"
                        className="text-base sm:text-lg font-semibold text-[#193456]"
                    >
                        Crop your Corporate Headshot
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">
                        Drag the box to choose the part of the photo to use. Square or portrait only.
                    </p>
                </div>

                <div
                    className="flex-1 overflow-y-auto overflow-x-hidden bg-slate-50"
                    style={{ touchAction: "pinch-zoom" }}
                >
                    <style>{TOUCH_HANDLE_CSS}</style>
                    <div className="min-h-full p-4 flex items-center justify-center">
                        {imageSrc && (
                            <ReactCrop
                                crop={crop}
                                onChange={handleCropChange}
                                onComplete={(c) => setCompletedCrop(clampToSquareOrPortrait(c))}
                                minWidth={minDisplay.w}
                                minHeight={minDisplay.h}
                                keepSelection
                            >
                                <img
                                    ref={imgRef}
                                    src={imageSrc}
                                    alt="To crop"
                                    onLoad={handleImageLoad}
                                    className="block max-w-[calc(100vw-48px)] md:max-w-[640px]"
                                />
                            </ReactCrop>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="px-4 sm:px-6 py-2 bg-[#fff176]/40 border-t border-[#ffbe00] text-xs text-[#193456]">
                        {error}
                    </div>
                )}

                <div className="px-4 py-3 sm:px-6 sm:py-4 border-t border-slate-200 flex justify-end gap-2">
                    <Button variant="secondary" onClick={onCancel} disabled={isProcessing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApply}
                        disabled={isProcessing || !completedCrop || !completedCrop.width}
                    >
                        {isProcessing ? "Processing..." : "Apply"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default CorporateHeadshotCropModal;
